import { useState, useEffect } from 'react';
import { apiService } from '../services/api';

export interface TrendingEvent {
  id: number;
  title: string;
  description: string;
  location: string;
  latitude: string;
  longitude: string;
  startDate: string;
  endDate: string;
  expectedAttendance: number;
  isVerified: boolean;
  isActive: boolean;
  isFeatured: boolean;
  thumbnailBase64?: string;
  thumbnailMimeType?: string;
  createdAt: string;
  updatedAt: string;
  // Legacy fields for compatibility
  TITLE?: string;
  DESCRIPTION?: string;
  LOCATION?: string;
  LATITUDE?: string;
  LONGITUDE?: string;
  START_DATE?: string;
  END_DATE?: string;
  EXPECTED_ATTENDANCE?: number;
  IS_VERIFIED?: boolean;
  IS_ACTIVE?: boolean;
  IS_FEATURED?: boolean;
  IMAGES?: string[];
  ADDRESS?: string;
  address?: string;
  EVENT_ID?: number;
  FEATURED_AT?: string;
  featuredAt?: string;
}

export interface UseTrendingEventsParams {
  page?: number;
  limit?: number;
  search?: string;
  startDate?: string;
  endDate?: string;
  category?: string;
  isFeatured?: boolean;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

export interface UseTrendingEventsReturn {
  events: TrendingEvent[];
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  } | null;
  refetch: () => void;
}

export const useTrendingEvents = (params?: UseTrendingEventsParams): UseTrendingEventsReturn => {
  const [events, setEvents] = useState<TrendingEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<{
    page: number;
    limit: number;
    total: number;
    pages: number;
  } | null>(null);

  const fetchTrendingEvents = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔥 Fetching trending events with params:', params);

      const response = await apiService.getTrendingEvents(params);

      console.log('🔥 Trending events API response:', response);

      if (response.success !== false) {
        // Handle different response structures
        let eventData: TrendingEvent[] = [];
        
        if (response.data && Array.isArray(response.data)) {
          eventData = response.data;
        } else if (Array.isArray(response)) {
          eventData = response;
        } else if (response.events && Array.isArray(response.events)) {
          eventData = response.events;
        } else {
          console.warn('🔥 Unexpected trending events response structure:', response);
        }

        setEvents(eventData);

        // Set pagination if available
        if (response.pagination) {
          setPagination(response.pagination);
        }

        console.log(`🔥 Successfully loaded ${eventData.length} trending events`);
      } else {
        throw new Error(response.message || 'Failed to fetch trending events');
      }
    } catch (err) {
      console.error('🔥 Error fetching trending events:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch trending events');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrendingEvents();
  }, [
    params?.page,
    params?.limit,
    params?.search,
    params?.startDate,
    params?.endDate,
    params?.category,
    params?.isFeatured,
    params?.sortBy,
    params?.sortOrder,
  ]);

  return {
    events,
    loading,
    error,
    pagination,
    refetch: fetchTrendingEvents,
  };
};
